import { PushNotifications } from '@capacitor/push-notifications';
import { LocalNotifications } from '@capacitor/local-notifications';
import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';
import { App } from '@capacitor/app';
import { registerAppDevice } from '../../actions/firebase/CloudMessagingActions';
import { SERVER_URL } from '../../utils/Constants';

/** Must match MainActivity.FCM_CHANNEL_ID and AndroidManifest meta-data. */
const FCM_CHANNEL_ID = 'fcm_default_channel';

let _pushReceivedCount = 0;
let _pushStore = null;
let _pushBrandId = null;
let _pushDeviceId = null;
let _pushPlatform = null;
let _pushListenersAttached = false;
let _pushResumeListenerAttached = false;
let _lastSyncedToken = null;

function tokenPreview(token) {
    if (!token || token.length < 12) {
        return token || '(empty)';
    }
    return `${token.slice(0, 8)}...${token.slice(-8)}`;
}

function dispatchTokenToBackend(brandId, token, uuid, platform) {
    if (!_pushStore || !token) {
        console.warn('[Push] Skipping device-info sync — missing store or token');
        return;
    }
    if (token === _lastSyncedToken) {
        console.log('[Push] Skipping device-info sync — token unchanged');
        return;
    }
    _lastSyncedToken = token;
    console.log('[Push] Syncing FCM token with backend', {
        brandId,
        deviceId: uuid,
        platform,
        api: SERVER_URL,
        token: tokenPreview(token)
    });
    _pushStore.dispatch(registerAppDevice(brandId, token, uuid, platform));
}

async function ensureAndroidNotificationChannel() {
    if (Capacitor.getPlatform() !== 'android') {
        return;
    }
    await LocalNotifications.createChannel({
        id: FCM_CHANNEL_ID,
        name: 'Push Notifications',
        importance: 4
    });
}

async function requestPushPermission() {
    let permStatus = await PushNotifications.checkPermissions();
    if (permStatus.receive === 'prompt') {
        permStatus = await PushNotifications.requestPermissions();
    }
    return permStatus.receive;
}

async function attachPushListeners(brandId, uuid, platform) {
    if (_pushListenersAttached) {
        return;
    }

    await PushNotifications.removeAllListeners();

    await PushNotifications.addListener('registration', (token) => {
        dispatchTokenToBackend(brandId, token.value, uuid, platform);
    });

    await PushNotifications.addListener('registrationError', (error) => {
        console.error('[Push] registrationError:', error);
    });

    await PushNotifications.addListener('pushNotificationReceived', async (notification) => {
        _pushReceivedCount += 1;
        const capacitorPlatform = Capacitor.getPlatform();
        const data = notification.data || {};
        const isDataOnly = !notification.title && !notification.body;
        if (!isDataOnly) {
            return;
        }

        const notificationId = Date.now() % 2147483647;
        const localNotification = {
            title: notification.title || data.title || '',
            body: notification.body || data.body || '',
            id: notificationId,
            channelId: FCM_CHANNEL_ID,
            extra: data
        };

        if (capacitorPlatform === 'ios' && data.image) {
            localNotification.attachments = [{ id: 'image', url: data.image }];
        }

        await LocalNotifications.schedule({ notifications: [localNotification] });
    });

    _pushListenersAttached = true;
}

async function registerForPush() {
    console.log('[Push] Calling PushNotifications.register()');
    await PushNotifications.register();
}

async function logNativeBuildInfo() {
    try {
        const appInfo = await App.getInfo();
        console.log('[Push] Native app info', {
            id: appInfo.id,
            name: appInfo.name,
            version: appInfo.version,
            build: appInfo.build
        });
    } catch (e) {
        console.warn('[Push] Could not read native app info:', e);
    }
}

async function completePushRegistration(store, brandId) {
    _pushStore = store;
    _pushBrandId = brandId;

    const deviceInfo = await Device.getId();
    const platform = Capacitor.getPlatform() === 'android' ? 'Android' : 'iOS';
    const uuid = deviceInfo.identifier;
    _pushDeviceId = uuid;
    _pushPlatform = platform;

    const permission = await requestPushPermission();
    if (permission !== 'granted') {
        console.warn(
            '[Push] Notification permission not granted:',
            permission,
            '— enable in Settings → Apps →',
            (await App.getInfo()).name,
            '→ Notifications, then reopen the app'
        );
        return false;
    }

    await ensureAndroidNotificationChannel();
    await attachPushListeners(brandId, uuid, platform);
    await registerForPush();
    return true;
}

function attachResumeListener(store, brandId) {
    if (_pushResumeListenerAttached) {
        return;
    }
    _pushResumeListenerAttached = true;

    App.addListener('resume', () => {
        void (async () => {
            const permission = await PushNotifications.checkPermissions();
            if (permission.receive !== 'granted') {
                console.warn('[Push] Still no notification permission on resume:', permission.receive);
                return;
            }
            if (!_pushListenersAttached) {
                await completePushRegistration(store, brandId);
            }
        })();
    });
}

/**
 * Initialise native push at app startup.
 * Retries on resume only if the first attempt failed (e.g. permission denied at launch).
 */
export async function setupNativePushNotifications(store, brandId) {
    if (!Capacitor.isNativePlatform() || !store || !brandId) {
        return;
    }

    await logNativeBuildInfo();
    attachResumeListener(store, brandId);
    await completePushRegistration(store, brandId);
}

/** @deprecated Prefer setupNativePushNotifications at app startup. */
export const initiateAppDeviceCapacitor = async (brandId, callback) => {
    if (!Capacitor.isNativePlatform()) {
        return;
    }

    const deviceInfo = await Device.getId();
    const platform = Capacitor.getPlatform() === 'android' ? 'Android' : 'iOS';
    const uuid = deviceInfo.identifier;

    const permission = await requestPushPermission();
    if (permission !== 'granted') {
        return;
    }

    await ensureAndroidNotificationChannel();
    await PushNotifications.removeAllListeners();

    await PushNotifications.addListener('registration', (token) => {
        callback(brandId, token.value, uuid, platform);
    });

    await PushNotifications.addListener('registrationError', (error) => {
        console.error('[Push] registrationError:', error);
    });

    await registerForPush();
};

export const allowIOSNotificationsCapacitor = async (callback) => {
    const capacitorPlatform = Capacitor.getPlatform();
    if (capacitorPlatform === 'ios') {
        await requestPushPermission();
    }
    callback();
};
